import * as React from 'react';
import type { IdentityOcr, IdentityOcrResult, MisafirGirdi } from '@hotelcms/shared';
import { Button } from '@/components/ui/button';
import { Kart, KartBaslik, KartGovde, Rozet } from '@/components/ui/card';
import { Alan } from '@/components/ui/field';
import { ApiHatasi, api } from '@/lib/api';
import { yeniIstekId } from '@/lib/offline';
import { paraFormatla, tarihFormatla } from '@/lib/utils';
import { TelefonEslesme } from '@/features/scanner/TelefonEslesme';
import { ArkaYuzTamamla } from './ArkaYuzTamamla';
import { KimlikYakala } from './KimlikYakala';
import { MisafirFormu, ocrdanMisafir } from './MisafirFormu';

export interface CheckInRezervasyon {
  id: string;
  kod: string;
  girisTarihi: string;
  cikisTarihi: string;
  odaNo: string | null;
  roomId: string | null;
  odaTipi: string;
  toplamKurus: number;
  paraBirimi: string;
  yetiskin: number;
  cocuk: number;
  pansiyon: string;
}

interface Props {
  rezervasyon: CheckInRezervasyon;
  bosOdalar: { id: string; no: string; temizlikDurumu: string }[];
  onTamamlandi: (sonuc: { reservationId: string; odaNo: string | null }) => void;
  onKapat: () => void;
  /**
   * Telefonla tarama eslesmesi icin gerekli. Verilmezse dugme cizilmez -
   * ozellik istege bagli oldugu icin eksikligi ekrani bozmaz (testler ve
   * eski cagri yerleri bu yuzden degismek zorunda kalmaz).
   */
  tenantId?: string | null;
}

type Adim = 1 | 2 | 3;

/** Bir misafirin girdisi + okunan belgesi. Belge KBS'de kisiye baglidir. */
interface MisafirKaydi {
  girdi: MisafirGirdi;
  ocr: IdentityOcrResult | null;
  onizleme: string | null;
  /**
   * "Elle gir" secildi mi.
   *
   * Formun gorunurlugu AYRI bir durumdur, adin dolu olmasi degil. Onceden
   * kosul `ocr || misafir.ad` idi: elle girise basinca ad bos oldugu icin
   * form hic acilmiyordu. Adi bir bayrak yerine kullanmak, ada bosluk gibi
   * seyler yazmayi da gerektirirdi - o bosluk KBS bildirimine kadar giderdi.
   */
  elleGiris: boolean;
}

const ADIM_BASLIK: Record<Adim, string> = {
  1: 'Kimlik',
  2: 'Oda ve ücret',
  3: 'Onay ve KBS',
};

/**
 * Check-in ekrani - TEK EKRAN, UC ADIM.
 *
 *  1) Kimligi kameraya goster -> alanlar otomatik dolar
 *  2) Oda / ucret onayla
 *  3) KBS'ye gonder
 *
 * Klavye: Enter = ileri, Esc = geri, F2 = fotograf cek.
 * Internet yoksa kayit cihazda tutulur, baglanti gelince kendiliginden gider.
 */
export function CheckInScreen({ rezervasyon, bosOdalar, onTamamlandi, onKapat, tenantId }: Props) {
  /*
   * KBS her misafir icin AYRI bildirim ister; rezervasyon 3 kisiyse uc
   * kimlik alinmalidir. Onceden ekran her zaman TEK misafir gonderiyordu -
   * API dizi kabul ettigi halde - ve uc kisilik konaklamada iki kisi
   * bildirilmeden kaliyordu. Adim 1 artik kisi kisi ilerler.
   */
  const kisiSayisi = Math.max(1, rezervasyon.yetiskin + rezervasyon.cocuk);
  const [adim, setAdim] = React.useState<Adim>(1);
  const [misafirler, setMisafirler] = React.useState<MisafirKaydi[]>(() =>
    Array.from({ length: kisiSayisi }, () => ({
      girdi: ocrdanMisafir(null),
      ocr: null,
      onizleme: null,
      elleGiris: false,
    })),
  );
  /** Adim 1'de su an kimin kimligi aliniyor (0 tabanli). */
  const [sira, setSira] = React.useState(0);
  const [odaId, setOdaId] = React.useState<string | null>(rezervasyon.roomId);
  const [tutarKurus, setTutarKurus] = React.useState(rezervasyon.toplamKurus);
  const [kvkkOnay, setKvkkOnay] = React.useState(false);
  const [gonderiliyor, setGonderiliyor] = React.useState(false);
  const [hata, setHata] = React.useState<string | null>(null);
  const [cevrimdisiKuyruk, setCevrimdisiKuyruk] = React.useState(false);

  const aktif = misafirler[sira]!;
  const doluMu = (m: MisafirKaydi) => m.girdi.ad.trim().length > 0 && m.girdi.soyad.trim().length > 0;
  const kimlikTamam = doluMu(aktif);
  /** Adim 2'ye gecmek icin HERKESIN kimligi girilmis olmali (KBS sarti). */
  const hepsiTamam = misafirler.every(doluMu);
  const sonKisi = sira === kisiSayisi - 1;
  const odaTamam = Boolean(odaId);

  function kaydiGuncelle(i: number, yama: Partial<MisafirKaydi>) {
    setMisafirler((o) => o.map((m, k) => (k === i ? { ...m, ...yama } : m)));
  }

  function ocrSonucu(sonuc: IdentityOcrResult, gorselUrl: string) {
    kaydiGuncelle(sira, { ocr: sonuc, onizleme: gorselUrl, girdi: ocrdanMisafir(sonuc) });
  }

  /*
   * Telefondan gelen kimlik.
   *
   * Hedef misafiri telefonun bildirdigi SIRA belirler, ekranin o anki
   * secimi degil: gorevli masaya donup baska bir kisiye gecmis olabilir ve
   * gelen belge o kisiye yazilirsa KBS'ye yanlis kisi bildirilir.
   * Onizleme yoktur - fotograf telefonda kaldi, masaya yalnizca alanlar geldi.
   */
  const telefondanKimlik = React.useCallback((hedefSira: number, sonuc: IdentityOcrResult) => {
    setMisafirler((o) =>
      o.map((m, k) =>
        k === hedefSira ? { ...m, ocr: sonuc, onizleme: null, girdi: ocrdanMisafir(sonuc) } : m,
      ),
    );
    setSira(hedefSira);
  }, []);

  /**
   * Arka yuzden gelen alanlari mevcut kayda birlestirir.
   *
   * Kural: YALNIZCA bos alanlar doldurulur. Dolu bir alanin uzerine yazmak,
   * gorevlinin elle duzelttigi bir degeri ikinci bir OCR tahminiyle bozardi.
   */
  function arkaYuzuBirlestir(i: number, ek: IdentityOcr) {
    setMisafirler((o) =>
      o.map((m, k) => {
        if (k !== i) return m;
        const bos = (v: string | null | undefined) => v === null || v === undefined || v === '';
        const g = m.girdi;
        return {
          ...m,
          // OCR sonucunu da tazele ki "arka yuz gerekli" uyarisi kalksin.
          ocr: m.ocr
            ? {
                ...m.ocr,
                alanlar: {
                  ...m.ocr.alanlar,
                  anneAdi: m.ocr.alanlar.anneAdi ?? ek.anneAdi,
                  babaAdi: m.ocr.alanlar.babaAdi ?? ek.babaAdi,
                  dogumYeri: m.ocr.alanlar.dogumYeri ?? ek.dogumYeri,
                },
                dogrulama: {
                  ...m.ocr.dogrulama,
                  arkaYuzGerekli:
                    bos(m.ocr.alanlar.anneAdi ?? ek.anneAdi) || bos(m.ocr.alanlar.babaAdi ?? ek.babaAdi),
                },
              }
            : m.ocr,
          girdi: {
            ...g,
            anneAdi: bos(g.anneAdi) ? ek.anneAdi : g.anneAdi,
            babaAdi: bos(g.babaAdi) ? ek.babaAdi : g.babaAdi,
            dogumYeri: bos(g.dogumYeri) ? ek.dogumYeri : g.dogumYeri,
            belgeSeriNo: bos(g.belgeSeriNo) ? ek.seriNo : g.belgeSeriNo,
          },
        };
      }),
    );
  }

  /** Sonraki kisiye gec; sonuncudaysa oda adimina. */
  function ileri() {
    if (sonKisi) setAdim(2);
    else setSira((s) => s + 1);
  }

  async function tamamla() {
    setGonderiliyor(true);
    setHata(null);
    const istekId = yeniIstekId();
    try {
      const sonuc = await api<{ reservationId: string; odaNo: string | null }>('/api/checkin', {
        method: 'POST',
        idempotencyKey: istekId,
        cevrimdisiKuyrukla: true,
        body: JSON.stringify({
          reservationId: rezervasyon.id,
          roomId: odaId,
          // Ilk kisi ana misafir; belge dizileri misafir sirasiyla HIZALI gider.
          misafirler: misafirler.map((m, i) => ({ ...m.girdi, anaMisafir: i === 0 })),
          kvkkOnay,
          kvkkMetinSurumu: 'v1',
          belgeR2Keys: misafirler.map((m) => m.ocr?.meta.belgeR2Key ?? null),
          belgeTipleri: misafirler.map((m) => m.ocr?.alanlar.belgeTipi ?? 'BILINMIYOR'),
          cevrimdisiOlusturmaAni: Date.now(),
        }),
      });
      onTamamlandi(sonuc);
    } catch (e) {
      if (e instanceof ApiHatasi && e.kod === 'CEVRIMDISI') {
        setCevrimdisiKuyruk(true);
        onTamamlandi({ reservationId: rezervasyon.id, odaNo: bosOdalar.find((o) => o.id === odaId)?.no ?? null });
        return;
      }
      setHata(e instanceof ApiHatasi ? e.message : 'Beklenmeyen bir sorun oluştu.');
    } finally {
      setGonderiliyor(false);
    }
  }

  // Klavye kisayollari
  React.useEffect(() => {
    const dinleyici = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        adim === 1 ? onKapat() : setAdim((a) => (a - 1) as Adim);
      }
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (adim === 1 && kimlikTamam) setAdim(2);
        else if (adim === 2 && odaTamam) setAdim(3);
        else if (adim === 3 && kvkkOnay) void tamamla();
      }
    };
    window.addEventListener('keydown', dinleyici);
    return () => window.removeEventListener('keydown', dinleyici);
  });

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-5 p-4 sm:p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Check-in</h1>
          <p className="text-slate-600">
            {rezervasyon.kod} - {tarihFormatla(rezervasyon.girisTarihi)} / {tarihFormatla(rezervasyon.cikisTarihi)} -{' '}
            {rezervasyon.yetiskin} yetiskin{rezervasyon.cocuk > 0 ? `, ${rezervasyon.cocuk} cocuk` : ''}
          </p>
        </div>
        <Button varyant="sessiz" boyut="md" onClick={onKapat} kisayol="Esc">
          Kapat
        </Button>
      </header>

      {/* Adim gostergesi */}
      <ol className="flex items-center gap-2">
        {([1, 2, 3] as Adim[]).map((n) => (
          <li key={n} className="flex flex-1 items-center gap-2">
            <span
              className={
                'flex size-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ' +
                (adim === n ? 'bg-marka-600 text-white' : adim > n ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600')
              }
            >
              {adim > n ? '✓' : n}
            </span>
            <span className={'text-sm font-medium ' + (adim === n ? 'text-slate-900' : 'text-slate-500')}>
              {ADIM_BASLIK[n]}
            </span>
            {n < 3 && <span className="h-px flex-1 bg-slate-200" />}
          </li>
        ))}
      </ol>

      {hata && <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-red-800">{hata}</div>}
      {cevrimdisiKuyruk && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900">
          Internet baglantisi yok. Check-in cihaza kaydedildi, baglanti gelince kendiliginden gonderilecek.
        </div>
      )}

      {adim === 1 && (
        <div className="flex flex-col gap-5">
          {/*
            Kisi secici. Uc kisilik bir konaklamada resepsiyonist kimin
            kimligini aldigini ve kacinin kaldigini gormeli; sirayi bozup
            araya donmek de mumkun olmali (ikinci kisinin belgesi cantada
            kalmis olabilir).
          */}
          {kisiSayisi > 1 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-slate-700">
                Misafir {sira + 1} / {kisiSayisi}
              </span>
              <div className="flex flex-wrap gap-1.5">
                {misafirler.map((m, i) => (
                  <button
                    key={i}
                    onClick={() => setSira(i)}
                    aria-current={i === sira || undefined}
                    className={
                      'flex items-center gap-1.5 rounded-full border-[1.5px] px-3 py-1.5 text-[13px] font-semibold transition-colors ' +
                      (i === sira
                        ? 'border-marka-600 bg-marka-600 text-white'
                        : doluMu(m)
                          ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                          : 'border-slate-300 bg-white text-slate-500')
                    }
                  >
                    {doluMu(m) && i !== sira && <span aria-hidden="true">✓</span>}
                    {m.girdi.ad.trim() ? `${m.girdi.ad} ${m.girdi.soyad}`.trim() : `${i + 1}. kişi`}
                  </button>
                ))}
              </div>
              <span className="ml-auto text-[13px] text-slate-500">
                KBS her misafir için ayrı bildirim ister
              </span>
            </div>
          )}

          <Kart>
            <KartGovde>
              {aktif.ocr || aktif.elleGiris || aktif.girdi.ad ? (
                <div className="flex flex-col gap-4">
                  {aktif.onizleme && (
                    <div className="flex items-center gap-4">
                      <img src={aktif.onizleme} alt="Okunan belge" className="h-24 rounded-lg border border-slate-200 object-cover" />
                      <Button
                        varyant="ikincil"
                        onClick={() =>
                          kaydiGuncelle(sira, {
                            ocr: null,
                            onizleme: null,
                            girdi: ocrdanMisafir(null),
                            elleGiris: false,
                          })
                        }
                      >
                        Tekrar oku
                      </Button>
                    </div>
                  )}
                  {/*
                    Arka yuz atlanmis ya da okunamamissa: elle yazdirmak
                    yerine yalnizca arka yuzu okutup bos alanlari doldur.
                  */}
                  {aktif.ocr?.dogrulama.arkaYuzGerekli && (
                    <ArkaYuzTamamla
                      reservationId={rezervasyon.id}
                      onTamamlandi={(ek) => arkaYuzuBirlestir(sira, ek)}
                    />
                  )}
                  <MisafirFormu
                    ocr={aktif.ocr}
                    deger={aktif.girdi}
                    onChange={(g) => kaydiGuncelle(sira, { girdi: g })}
                  />
                </div>
              ) : (
                <KimlikYakala
                  reservationId={rezervasyon.id}
                  onSonuc={ocrSonucu}
                  onManuel={() => kaydiGuncelle(sira, { elleGiris: true })}
                />
              )}
            </KartGovde>
          </Kart>

          {/*
            Telefonla tarama - ISTEGE BAGLI.
            Masadaki kamera kotuyse gorevli kendi telefonunu kamera olarak
            kullanir. Telefon check-in'i tamamlayamaz; yalnizca kimlik besler.
          */}
          {tenantId && (
            <TelefonEslesme
              reservationId={rezervasyon.id}
              rezKodu={rezervasyon.kod}
              odaNo={rezervasyon.odaNo}
              kisiSayisi={kisiSayisi}
              sira={sira}
              tenantId={tenantId}
              onKimlik={telefondanKimlik}
            />
          )}

          <div className="flex flex-wrap items-center justify-end gap-3">
            {kisiSayisi > 1 && !hepsiTamam && sonKisi && (
              <span className="mr-auto text-[13px] text-amber-800">
                {misafirler.filter((m) => !doluMu(m)).length} misafirin kimliği eksik.
              </span>
            )}
            <Button
              boyut="xl"
              disabled={sonKisi ? !hepsiTamam : !kimlikTamam}
              onClick={ileri}
              kisayol="Ctrl+↵"
            >
              {sonKisi ? 'Devam' : `Sonraki misafir (${sira + 2}/${kisiSayisi})`}
            </Button>
          </div>
        </div>
      )}

      {adim === 2 && (
        <div className="flex flex-col gap-5">
          <Kart>
            <KartBaslik>
              <h2 className="text-lg font-semibold">Oda seçimi</h2>
              <Rozet renk="bilgi">{rezervasyon.odaTipi}</Rozet>
            </KartBaslik>
            <KartGovde className="grid gap-3 sm:grid-cols-3">
              {bosOdalar.map((o) => (
                <button
                  key={o.id}
                  onClick={() => setOdaId(o.id)}
                  className={
                    'flex h-20 flex-col items-center justify-center rounded-xl border-2 text-lg font-semibold transition ' +
                    (odaId === o.id
                      ? 'border-marka-600 bg-marka-50 text-marka-700'
                      : 'border-slate-200 bg-white text-slate-800 hover:border-slate-300')
                  }
                >
                  {o.no}
                  <span className="text-xs font-normal text-slate-500">
                    {o.temizlikDurumu === 'TEMIZ' ? 'Temiz' : 'Temizlik bekliyor'}
                  </span>
                </button>
              ))}
              {bosOdalar.length === 0 && (
                <p className="col-span-full text-slate-600">Bu oda tipinde uygun oda görünmüyor.</p>
              )}
            </KartGovde>
          </Kart>

          <Kart>
            <KartBaslik>
              <h2 className="text-lg font-semibold">Ücret</h2>
              <Rozet>{rezervasyon.pansiyon}</Rozet>
            </KartBaslik>
            <KartGovde className="grid gap-4 sm:grid-cols-2">
              <Alan
                etiket="Toplam tutar"
                inputMode="decimal"
                value={(tutarKurus / 100).toString()}
                ipucu={`Sozlesme fiyati: ${paraFormatla(rezervasyon.toplamKurus, rezervasyon.paraBirimi)}`}
                onChange={(e) => setTutarKurus(Math.round(Number(e.target.value.replace(',', '.') || 0) * 100))}
              />
              <div className="flex items-end">
                <p className="text-2xl font-bold text-slate-900">{paraFormatla(tutarKurus, rezervasyon.paraBirimi)}</p>
              </div>
            </KartGovde>
          </Kart>

          <div className="flex justify-between">
            <Button varyant="sessiz" boyut="xl" onClick={() => setAdim(1)}>
              Geri
            </Button>
            <Button boyut="xl" disabled={!odaTamam} onClick={() => setAdim(3)} kisayol="Ctrl+↵">
              Devam
            </Button>
          </div>
        </div>
      )}

      {adim === 3 && (
        <div className="flex flex-col gap-5">
          <Kart>
            <KartBaslik>
              <h2 className="text-lg font-semibold">Özet</h2>
            </KartBaslik>
            <KartGovde className="grid gap-3 sm:grid-cols-2">
              {/* Ozet TUM misafirleri listeler: kacinin bildirilecegi burada gorunur. */}
              <Ozet
                etiket={kisiSayisi > 1 ? `Misafirler (${kisiSayisi})` : 'Misafir'}
                deger={misafirler.map((m) => `${m.girdi.ad} ${m.girdi.soyad}`.trim()).join(', ')}
              />
              <Ozet etiket="Uyruk" deger={[...new Set(misafirler.map((m) => m.girdi.uyruk))].join(', ')} />
              <Ozet etiket="Oda" deger={bosOdalar.find((o) => o.id === odaId)?.no ?? '-'} />
              <Ozet etiket="Tutar" deger={paraFormatla(tutarKurus, rezervasyon.paraBirimi)} />
              <Ozet etiket="Giris" deger={tarihFormatla(rezervasyon.girisTarihi)} />
              <Ozet etiket="Cikis" deger={tarihFormatla(rezervasyon.cikisTarihi)} />
            </KartGovde>
          </Kart>

          {(() => {
            // Eksik KBS alanlari kisi kisi toplanir; hangi misafirde eksik oldugu yazilir.
            const eksikler = misafirler
              .map((m, i) => ({
                kisi: `${m.girdi.ad} ${m.girdi.soyad}`.trim() || `${i + 1}. kişi`,
                alanlar: m.ocr?.dogrulama.kbsEksikAlanlar ?? [],
              }))
              .filter((e) => e.alanlar.length > 0);
            if (eksikler.length === 0) return null;
            return (
              <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900">
                KBS bildirimi için eksik alanlar var:
                <ul className="mt-1 list-disc pl-5">
                  {eksikler.map((e) => (
                    <li key={e.kisi}>
                      <strong>{e.kisi}</strong>: {e.alanlar.join(', ')}
                    </li>
                  ))}
                </ul>
                Şimdi gönderebilirsiniz; eksikler KBS panelinde kırmızı görünür ve oradan tamamlanabilir.
              </div>
            );
          })()}

          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-white p-4">
            <input
              type="checkbox"
              checked={kvkkOnay}
              onChange={(e) => setKvkkOnay(e.target.checked)}
              className="mt-1 size-5 rounded border-slate-300"
            />
            <span className="text-sm text-slate-700">
              Misafire KVKK aydinlatma metni okutuldu. Kimlik bilgileri 1774 sayili kanun geregi Kimlik Bildirim
              Sistemi'ne bildirilecektir.
            </span>
          </label>

          <div className="flex justify-between">
            <Button varyant="sessiz" boyut="xl" onClick={() => setAdim(2)}>
              Geri
            </Button>
            <Button
              varyant="basari"
              boyut="xl"
              disabled={!kvkkOnay}
              yukleniyor={gonderiliyor}
              onClick={tamamla}
              kisayol="Ctrl+↵"
            >
              Check-in yap ve KBS'ye gonder
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Ozet({ etiket, deger }: { etiket: string; deger: string }) {
  return (
    <div>
      <dt className="text-sm text-slate-500">{etiket}</dt>
      <dd className="text-lg font-semibold text-slate-900">{deger}</dd>
    </div>
  );
}
